
import React, { useState, useEffect } from "react";
import { CardContent, CardFooter } from "@/components/ui/card";
import { PasswordInput } from "@/components/ui/password-input";
import { Small } from "@/components/ui/typography";
import AuthButton from "../AuthButton";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Label component that uses Typography internally
const Label = ({ htmlFor, children }: { htmlFor: string, children: React.ReactNode }) => (
  <Small weight="medium" className="text-mono-seasalt block mb-1.5">
    {children}
  </Small>
);

type PasswordResetFormProps = {
  onSubmit: (password: string, confirmPassword: string) => Promise<void>;
  loading: boolean;
  errorMessage: string;
};

const PasswordResetForm = ({ onSubmit, loading, errorMessage }: PasswordResetFormProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState("");
  const navigate = useNavigate();

  // Clear validation errors when inputs change, but preserve server errors
  useEffect(() => {
    if (validationError) {
      setValidationError("");
    }
  }, [password, confirmPassword]);

  const validatePasswords = () => {
    let isValid = true;
    
    if (password.length < 6) {
      setValidationError("Password must be at least 6 characters");
      isValid = false;
    } else if (password !== confirmPassword) {
      setValidationError("Passwords don't match");
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");
    
    if (!validatePasswords()) {
      return;
    }
    
    await onSubmit(password, confirmPassword);
  };

  // Fixed handler to correctly navigate back to login
  const handleBackToLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/auth?mode=login", { replace: true });
  };

  // Determine which error message to show - validation errors take precedence over server errors
  const displayError = validationError || errorMessage;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <CardContent className="space-y-4 pt-4 pb-4">
        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <PasswordInput
            id="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="bg-mono-onyx border-mono-slateGray/30 transition-colors"
            minLength={6}
            showPasswordState={[showPassword, setShowPassword]}
            autoComplete="new-password"
          />
          <Small variant="subtle" className="text-mono-frenchGray block text-xs">
            Password must be at least 6 characters long
          </Small>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm Password</Label>
          <PasswordInput
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            required
            className="bg-mono-onyx border-mono-slateGray/30 transition-colors"
            showPasswordState={[showPassword, setShowPassword]}
            autoComplete="new-password"
          />
        </div>
      </CardContent>
      
      <CardFooter className="flex flex-col gap-4 pt-4 pb-8">
        {displayError && (
          <Alert variant="destructive" className="bg-red-500/10 border border-red-500/20 p-3 mt-0 mb-4 w-full">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 pt-0.5" />
              <AlertDescription className="text-red-500">{displayError}</AlertDescription>
            </div>
          </Alert>
        )}
        
        <AuthButton 
          type="submit" 
          loading={loading}
        >
          {loading ? "Updating password..." : "Update password"}
        </AuthButton>
        
        <div className="flex justify-center mt-2">
          <button 
            onClick={handleBackToLogin}
            className="text-mono-seasalt hover:text-mono-platinum transition-colors text-sm"
          >
            Back to login
          </button>
        </div>
      </CardFooter>
    </form>
  );
};

export default PasswordResetForm;
